import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconPencil, IconPlus, IconSend, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  ApiError,
  NAME_MAX_LENGTH,
  api,
  type ProjectSummary,
  type User,
  type UserInviteRequest,
} from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

/**
 * Names are editable in place; role and grants are set once, at invite time
 * (§6.2), and cannot yet be changed after the fact — that arrives with the
 * audit view later in phase 5.
 *
 * Inline rather than a modal: it is one short field, and a dialog to change a
 * name costs two extra clicks and hides the rest of the table while you do it.
 */
export function AdminUsersPage() {
  const { data, isLoading, error } = useQuery<User[]>({
    queryKey: ["admin", "users"],
    queryFn: () => api.get<User[]>("/api/admin/users"),
  });

  if (isLoading) return <Loader />;
  if (error) {
    return (
      <Alert color="red" title="Could not load users">
        {error instanceof Error ? error.message : "Unexpected error."}
      </Alert>
    );
  }

  return (
    <>
      <Group justify="space-between" align="flex-end" mb="xs">
        <Title order={2}>Users</Title>
        <CreateUserButton />
      </Group>
      <Text c="dimmed" mb="lg">
        Creating a user emails them a link to set their own password. The link
        expires after 72 hours; resend it if it lapses.
      </Text>

      <Table.ScrollContainer minWidth={640}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data ?? []).map((user) => (
              <Table.Tr key={user.id}>
                <Table.Td>{user.email}</Table.Td>
                <Table.Td>
                  <NameCell user={user} />
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" color={user.system_role === "admin" ? "indigo" : "gray"}>
                    {user.system_role}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge
                    variant="light"
                    color={
                      user.status === "active"
                        ? "green"
                        : user.status === "invited"
                          ? "yellow"
                          : "red"
                    }
                  >
                    {user.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{user.status === "invited" && <ResendInviteAction user={user} />}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}

function ResendInviteAction({ user }: { user: User }) {
  const resend = useMutation({
    mutationFn: () => api.post<{ detail: string }>(`/api/admin/users/${user.id}/invite`),
    onSuccess: () =>
      notifications.show({ color: "green", message: `Invitation resent to ${user.email}.` }),
    onError: (err) =>
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not resend the invitation.",
      }),
  });

  return (
    <Button
      size="xs"
      variant="subtle"
      leftSection={<IconSend size={14} />}
      loading={resend.isPending}
      onClick={() => resend.mutate()}
    >
      Resend invite
    </Button>
  );
}

const ROLE_OPTIONS = [
  { value: "viewer", label: "Project Viewer" },
  { value: "admin", label: "Administrator" },
];

function CreateUserButton() {
  const [opened, { open, close }] = useDisclosure(false);
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<"admin" | "viewer">("viewer");
  const [projectSlugs, setProjectSlugs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Only fetched once the modal is open — no reason to load it on every visit
  // to a page most admins won't use every day.
  const projects = useQuery<ProjectSummary[]>({
    queryKey: ["projects"],
    queryFn: () => api.get<ProjectSummary[]>("/api/projects"),
    enabled: opened,
  });

  function reset() {
    setEmail("");
    setDisplayName("");
    setRole("viewer");
    setProjectSlugs([]);
    setError(null);
  }

  const invite = useMutation({
    mutationFn: (payload: UserInviteRequest) => api.post<User>("/api/admin/users", payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      notifications.show({ color: "green", message: `Invitation sent to ${created.email}.` });
      reset();
      close();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not create the user."),
  });

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    invite.mutate({
      email: email.trim(),
      display_name: displayName.trim() || null,
      system_role: role,
      // Admins already see every project (§6.3); sending grants for one would
      // just be inert rows.
      project_slugs: role === "admin" ? [] : projectSlugs,
    });
  }

  function handleClose() {
    reset();
    close();
  }

  return (
    <>
      <Button leftSection={<IconPlus size={16} />} onClick={open}>
        Create user
      </Button>
      <Modal opened={opened} onClose={handleClose} title="Create user">
        <form onSubmit={handleSubmit}>
          <Stack>
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
            <TextInput
              label="Email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              label="Name"
              description="Optional."
              maxLength={NAME_MAX_LENGTH}
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
            />
            <Select
              label="Role"
              data={ROLE_OPTIONS}
              value={role}
              allowDeselect={false}
              onChange={(value) => setRole((value as "admin" | "viewer") ?? "viewer")}
            />
            {role === "viewer" && (
              <MultiSelect
                label="Project access"
                description="Can be changed later from the CLI."
                placeholder={projects.data?.length ? "Select projects" : "No projects available"}
                data={(projects.data ?? []).map((p) => ({ value: p.slug, label: p.title }))}
                value={projectSlugs}
                onChange={setProjectSlugs}
                searchable
              />
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={handleClose} disabled={invite.isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={invite.isPending}>
                Send invitation
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}

function NameCell({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.display_name ?? "");

  const save = useMutation({
    mutationFn: (display_name: string | null) =>
      api.patch<User>(`/api/admin/users/${user.id}`, { display_name }),
    onSuccess: (updated) => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      // An admin editing their own row changes the name in the header too.
      if (updated.id === me?.user.id) {
        queryClient.invalidateQueries({ queryKey: ["me"] });
      }
    },
    onError: (err) =>
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not save the name.",
      }),
  });

  function commit() {
    const trimmed = value.trim();
    if (trimmed === (user.display_name ?? "")) {
      setEditing(false);
      return;
    }
    save.mutate(trimmed || null);
  }

  function cancel() {
    setValue(user.display_name ?? "");
    setEditing(false);
  }

  if (!editing) {
    return (
      <Group gap={6} wrap="nowrap">
        <Text size="sm" c={user.display_name ? undefined : "dimmed"}>
          {user.display_name ?? "—"}
        </Text>
        <ActionIcon
          variant="subtle"
          size="sm"
          aria-label={`Edit name for ${user.email}`}
          onClick={() => setEditing(true)}
        >
          <IconPencil size={14} />
        </ActionIcon>
      </Group>
    );
  }

  return (
    <Group gap={4} wrap="nowrap">
      <TextInput
        size="xs"
        w={200}
        autoFocus
        value={value}
        maxLength={NAME_MAX_LENGTH}
        placeholder="No name"
        disabled={save.isPending}
        aria-label={`Name for ${user.email}`}
        onChange={(event) => setValue(event.currentTarget.value)}
        // Enter saves and Escape reverts, so the row can be edited without
        // reaching for the mouse.
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") cancel();
        }}
      />
      <ActionIcon
        variant="subtle"
        color="green"
        size="sm"
        aria-label="Save name"
        loading={save.isPending}
        onClick={commit}
      >
        <IconCheck size={15} />
      </ActionIcon>
      <ActionIcon
        variant="subtle"
        color="gray"
        size="sm"
        aria-label="Cancel"
        disabled={save.isPending}
        onClick={cancel}
      >
        <IconX size={15} />
      </ActionIcon>
    </Group>
  );
}
